import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faGlobeAfrica } from '@fortawesome/free-solid-svg-icons';
import AZ from '../../assets/logos/az.png';

// Palette white / gold / glass
const colors = {
  goldLight: '#F5E1C8',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  whiteGlass: 'rgba(255, 255, 255, 0.7)',
  borderGlass: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 12px 28px rgba(184, 138, 86, 0.15)',
};

// Variantes d'animation
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const MainContainer = () => {
  const audioRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.volume = 0.1;
        audioRef.current.play().catch(() => {});
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{`
        .origins-section {
          position: relative;
          padding: 100px 0;
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 30%, #C69C6D 60%, #E8CFB5 100%);
          background-size: 400% 400%;
          animation: goldGradient 15s ease infinite;
          overflow: hidden;
          text-align: center;
        }

        @keyframes goldGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Orbes floues */
        .origins-section::before,
        .origins-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.4;
          z-index: 0;
          pointer-events: none;
        }

        .origins-section::before {
          width: 50vw;
          height: 50vw;
          background: #F5E1C8;
          top: -15%;
          left: -10%;
          animation: floatOrb 18s ease-in-out infinite;
        }

        .origins-section::after {
          width: 35vw;
          height: 35vw;
          background: #E8CFB5;
          bottom: -10%;
          right: -5%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        .container {
          position: relative;
          z-index: 2;
        }

        /* Logo glass */
        .logo-glass {
          display: inline-block;
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(198, 156, 109, 0.3);
          border-radius: 24px;
          padding: 20px 35px;
          box-shadow: 0 8px 24px rgba(107, 90, 70, 0.06);
          margin-bottom: 40px;
        }

        .logo-img {
          width: 220px;
          height: auto;
          filter: drop-shadow(0 4px 8px rgba(0,0,0,0.05));
        }

        .title-gold {
          font-size: clamp(2rem, 5vw, 2.8rem);
          font-weight: 700;
          color: ${colors.text};
          margin-bottom: 25px;
          letter-spacing: -0.5px;
        }

        .subtitle {
          font-size: 1.4rem;
          font-weight: 500;
          color: ${colors.textSoft};
          margin-bottom: 20px;
        }

        .highlight-name {
          font-size: clamp(2rem, 6vw, 3rem);
          font-weight: 700;
          background: linear-gradient(135deg, #5C3A1E, #8B5A2B, #C69C6D, #F5E1C8);
          background-size: 300% 300%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: goldTextShine 8s ease-in-out infinite;
          cursor: default;
          display: inline-block;
          transition: transform 0.3s;
        }

        .highlight-name:hover {
          transform: scale(1.05);
        }

        @keyframes goldTextShine {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .text-block {
          font-size: 1.2rem;
          color: ${colors.textSoft};
          line-height: 1.7;
          margin: 0 auto 20px;
          max-width: 800px;
        }

        .value-list {
          list-style: none;
          padding: 0;
          margin: 0 auto 30px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }

        .value-item {
          font-size: 1.2rem;
          font-weight: 500;
          color: ${colors.text};
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(198, 156, 109, 0.25);
          border-radius: 50px;
          padding: 12px 28px;
          transition: all 0.3s;
          box-shadow: 0 4px 12px rgba(107, 90, 70, 0.04);
        }

        .value-item:hover {
          background: rgba(255, 255, 255, 0.8);
          box-shadow: 0 8px 20px rgba(184, 138, 86, 0.1);
          border-color: ${colors.gold};
          transform: translateY(-2px);
        }

        .check-icon {
          color: ${colors.gold};
          font-size: 1.4rem;
        }

        .globe-icon {
          color: ${colors.goldDark};
          margin-right: 8px;
        }

        .world-text {
          font-size: 1.1rem;
          color: ${colors.textSoft};
          line-height: 1.6;
          margin-top: 10px;
        }

        @media (max-width: 768px) {
          .origins-section {
            padding: 70px 0;
          }
          .logo-glass {
            padding: 15px 20px;
          }
          .logo-img {
            width: 180px;
          }
          .value-item {
            padding: 10px 20px;
            font-size: 1rem;
          }
        }
      `}</style>

      <section className="origins-section">
        {/* Son d'ambiance discret */}
        <audio ref={audioRef} loop>
          <source src="/sounds/ambiance.mp3" type="audio/mpeg" />
        </audio>

        <div className="container">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Logo en verre */}
            <motion.div variants={itemVariants}>
              <div className="logo-glass">
                <img src={AZ} alt="Azyekos Logo" className="logo-img" />
              </div>
            </motion.div>

            {/* Titre principal */}
            <motion.h1 className="title-gold" variants={itemVariants}>
              LES ORIGINES DU NOM AZYEKOS
            </motion.h1>

            {/* Introduction */}
            <motion.p className="subtitle" variants={itemVariants}>
              Azyekos est l’assemblage des trois autres prénoms de la fondatrice de la société :
            </motion.p>

            {/* Nom mis en avant */}
            <motion.div variants={itemVariants}>
              <span className="highlight-name">AZALA YEMO KOSSO</span>
            </motion.div>

            {/* Description */}
            <motion.p className="text-block" variants={itemVariants}>
              L’idée de créer une conciergerie de luxe lui est venue grâce à ses précédentes expériences dans l’horeca et l’événementiel. Pour Azyekos Services, une conciergerie rassemble ces deux points et plus encore.
            </motion.p>

            {/* Liste de valeurs */}
            <motion.ul className="value-list" variants={itemVariants}>
              <li className="value-item">
                <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                Satisfaire ses clients quelle que soit leur demande.
              </li>
              <li className="value-item">
                <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                Les assister pleinement afin de leur faciliter tout accès à leurs souhaits.
              </li>
            </motion.ul>

            {/* Présence mondiale */}
            <motion.p className="world-text" variants={itemVariants}>
              <FontAwesomeIcon icon={faGlobeAfrica} className="globe-icon" />
              La société Azyekos Services est une société congolaise. Néanmoins, ses services restent présents partout dans le monde.
            </motion.p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default MainContainer;